/**
 * Phone presentation (D580). The domain stores a phone as E.164 (`@vello/contact`); a human reads it in the
 * country's own pattern. The *pattern* is a country-pack fact — a pack hands this module a `PhoneDisplayRule` (its
 * calling code plus how its national number is written); nothing here names a country, a calling code or a pattern.
 *
 * - The phone of the viewer's own country reads in the national pattern; a phone of any other country reads in the
 *   international pattern (`+CC …`).
 * - A country with no installed rule, or a number its rule cannot read, falls back to `+CC rest` (calling code
 *   split by the ITU E.164 assignment structure) — never a throw.
 * - A masked phone (the server hides digits behind a mask character) and anything that is not a plain E.164
 *   number come back exactly as received: this module formats what is safe, it never unmasks or guesses.
 */
const PLAIN_E164 = /^\+[1-9]\d{6,14}$/;
const SPACING = /[\s().-]/g;
/**
 * The length of the country calling code that starts `digits` (E.164 numbers, ITU-T assignment structure: codes are
 * prefix-free, 1 to 3 digits). A structural rule of the numbering plan, not a country table.
 */
export function callingCodeLength(digits) {
    const d1 = digits.charAt(0);
    const d2 = digits.slice(0, 2);
    if (d1 === '1' || d1 === '7')
        return 1;
    switch (d1) {
        case '2':
            return d2 === '20' || d2 === '27' ? 2 : 3;
        case '3':
            return ['30', '31', '32', '33', '34', '36', '39'].includes(d2) ? 2 : 3;
        case '4':
            return d2 === '42' ? 3 : 2;
        case '5':
            return d2 === '50' || d2 === '59' ? 3 : 2;
        case '6':
            return ['60', '61', '62', '63', '64', '65', '66'].includes(d2) ? 2 : 3;
        case '8':
            return ['81', '82', '84', '86'].includes(d2) ? 2 : 3;
        case '9':
            return ['90', '91', '92', '93', '94', '95', '98'].includes(d2) ? 2 : 3;
        default:
            return 3;
    }
}
/** `+CC rest` — the international fallback when no pack rule reads the number. */
function fallback(digits) {
    const cc = callingCodeLength(digits);
    return `+${digits.slice(0, cc)} ${digits.slice(cc)}`;
}
/**
 * Render an E.164 phone for a person. Returns the input untouched when it is masked, malformed or empty.
 */
export function formatPhone(raw, opts) {
    if (raw === undefined)
        return '';
    const text = raw.trim();
    // A mask character (anything but digits, `+` and phone punctuation) means the server withheld digits: keep it as sent.
    if (!/^[+\d\s().-]+$/.test(text))
        return raw;
    const compact = text.replace(SPACING, '');
    if (!PLAIN_E164.test(compact))
        return raw;
    const digits = compact.slice(1);
    // Calling codes are prefix-free, so at most one rule matches.
    const rule = opts.rules.find((r) => digits.startsWith(r.callingCode));
    if (rule !== undefined) {
        const national = digits.slice(rule.callingCode.length);
        const nationalView = opts.homeCountry !== undefined && opts.homeCountry.toUpperCase() === rule.countryCode.toUpperCase();
        const shown = nationalView ? rule.national(national) : rule.international(national);
        if (shown !== undefined)
            return nationalView ? shown : `+${rule.callingCode} ${shown}`;
    }
    return fallback(digits);
}
/**
 * The text a national-number field shows while a person types: the digits (anything else is dropped) written
 * progressively in the country's own pattern, capped at the rule's longest national number. A calling code with no
 * installed rule, or a rule without a `typing` pattern, leaves the plain digits. The field's *state* stays the
 * digits; this is only the view, re-derived from the digits on every change so the caret and backspace behave.
 */
export function formatPhoneTyping(nationalDigits, opts) {
    const calling = opts.callingCode.replace(/\D/g, '');
    const rule = calling === '' ? undefined : opts.rules.find((r) => r.callingCode === calling);
    let digits = nationalDigits.replace(/\D/g, '');
    if (rule?.maxNationalDigits !== undefined)
        digits = digits.slice(0, rule.maxNationalDigits);
    return rule?.typing !== undefined ? rule.typing(digits) : digits;
}
/** The character a hidden digit is drawn as. */
export const PHONE_MASK_CHAR = '*';
const DEFAULT_MASK_KEEP = { leading: 0, trailing: 4 };
/**
 * D717 (doc 29 G2): a person's OWN number with its middle digits hidden, in the country's pattern - `(34) 9****-1001` - so a screen that is seen over a shoulder
 * never shows the whole of it. The pattern and the digits kept are the country pack's (`PhoneDisplayRule.national` / `maskKeep`); a number no rule reads keeps
 * only its last four after the calling code. Anything that is not a plain E.164 number comes back as received (already masked or unreadable).
 */
export function maskPhone(raw, opts) {
    if (raw === undefined)
        return '';
    const compact = raw.trim().replace(SPACING, '');
    if (!PLAIN_E164.test(compact))
        return raw;
    const digits = compact.slice(1);
    const rule = opts.rules.find((r) => digits.startsWith(r.callingCode));
    const calling = rule?.callingCode ?? digits.slice(0, callingCodeLength(digits));
    const nationalCount = digits.length - calling.length;
    const keep = rule?.maskKeep ?? DEFAULT_MASK_KEEP;
    const shown = formatPhone(raw, opts);
    // Walk the formatted text: the calling code's digits (an international view) pass, then the national digits outside the kept ends are hidden.
    const international = shown.trimStart().startsWith('+');
    let seen = international ? -calling.length : 0;
    let out = '';
    for (const ch of shown) {
        if (/\d/.test(ch)) {
            const index = seen;
            seen += 1;
            out += index >= 0 && index >= keep.leading && index < nationalCount - keep.trailing ? PHONE_MASK_CHAR : ch;
        }
        else
            out += ch;
    }
    return out;
}
//# sourceMappingURL=phone.js.map